import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import type { ActionReturn } from "svelte/action";

export type { EmblaCarouselType };

export interface CarouselActionOptions {
    options?: EmblaOptionsType;
    onInit?: (api: EmblaCarouselType) => void;
    onSelect?: (index: number) => void;
    slideCount?: number;
    ariaLabel?: string;
}

export function carousel(
    node: HTMLElement,
    params: CarouselActionOptions,
): ActionReturn<CarouselActionOptions> {
    let emblaApi: EmblaCarouselType | null = null;
    let liveRegion: HTMLDivElement | null = null;
    let reducedMotionQuery: MediaQueryList | null = null;
    let reducedMotionHandler: ((e: MediaQueryListEvent) => void) | null = null;
    let emblaModulePromise: Promise<
        (node: HTMLElement, options?: EmblaOptionsType) => EmblaCarouselType
    > | null = null;
    let destroyed = false;
    let selectHandler: (() => void) | null = null;
    let keydownBound = false;

    function getSlideCount() {
        return params.slideCount ?? node.querySelectorAll("[data-carousel-slide]").length;
    }

    function announceSlide(index: number) {
        const total = getSlideCount();
        if (liveRegion) {
            liveRegion.textContent = `Slide ${index + 1} of ${total}`;
        }
    }

    function setupAria() {
        node.setAttribute("role", "region");
        node.setAttribute("aria-roledescription", "carousel");
        if (params.ariaLabel) {
            node.setAttribute("aria-label", params.ariaLabel);
        }

        const slides = node.querySelectorAll("[data-carousel-slide]");
        const total = slides.length;
        slides.forEach((slide, i) => {
            slide.setAttribute("role", "group");
            slide.setAttribute("aria-roledescription", "slide");
            slide.setAttribute("aria-label", `Slide ${i + 1} of ${total}`);
        });
    }

    function setupLiveRegion() {
        if (liveRegion) return;
        liveRegion = document.createElement("div");
        liveRegion.setAttribute("aria-live", "polite");
        liveRegion.setAttribute("aria-atomic", "true");
        liveRegion.className = "sr-only";
        liveRegion.style.cssText =
            "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;";
        node.appendChild(liveRegion);
    }

    function bindKeyboardNavigation() {
        if (keydownBound) return;
        node.addEventListener("keydown", handleKeydown);
        keydownBound = true;
    }

    function unbindKeyboardNavigation() {
        if (!keydownBound) return;
        node.removeEventListener("keydown", handleKeydown);
        keydownBound = false;
    }

    function destroyLiveRegion() {
        if (liveRegion?.parentNode) {
            liveRegion.parentNode.removeChild(liveRegion);
        }
        liveRegion = null;
    }

    function destroyEmbla() {
        if (!emblaApi) return;
        emblaApi.destroy();
        emblaApi = null;
        selectHandler = null;
    }

    function handleKeydown(e: KeyboardEvent) {
        if (!emblaApi) return;
        switch (e.key) {
            case "ArrowLeft":
                e.preventDefault();
                emblaApi.scrollPrev();
                break;
            case "ArrowRight":
                e.preventDefault();
                emblaApi.scrollNext();
                break;
            case "Home":
                e.preventDefault();
                emblaApi.scrollTo(0);
                break;
            case "End":
                e.preventDefault();
                emblaApi.scrollTo(getSlideCount() - 1);
                break;
        }
    }

    function bindSelectListener() {
        if (!emblaApi) return;
        if (selectHandler) {
            emblaApi.off("select", selectHandler);
        }
        selectHandler = () => {
            if (!emblaApi) return;
            const index = emblaApi.selectedScrollSnap();
            announceSlide(index);
            params.onSelect?.(index);
        };
        emblaApi.on("select", selectHandler);
        selectHandler();
    }

    function getEmblaOptions(): EmblaOptionsType {
        const usesReducedMotion = reducedMotionQuery?.matches ?? false;
        return {
            loop: true,
            ...(params.options ?? {}),
            ...(usesReducedMotion ? { duration: 0 } : {}),
        };
    }

    function getViewportTarget() {
        return (node.querySelector("[data-carousel-viewport]") as HTMLElement | null) ?? node;
    }

    async function loadEmbla() {
        emblaModulePromise ??= import("embla-carousel").then((module) => module.default);
        return emblaModulePromise;
    }

    async function createEmbla() {
        const EmblaCarousel = await loadEmbla();
        if (destroyed || getSlideCount() <= 1) return;

        emblaApi = EmblaCarousel(getViewportTarget(), getEmblaOptions());
        bindSelectListener();
        params.onInit?.(emblaApi);
    }

    async function refreshCarousel() {
        setupAria();
        if (getSlideCount() <= 1) {
            destroyEmbla();
            destroyLiveRegion();
            unbindKeyboardNavigation();
            if (reducedMotionQuery && reducedMotionHandler) {
                reducedMotionQuery.removeEventListener("change", reducedMotionHandler);
                reducedMotionHandler = null;
            }
            return;
        }

        setupLiveRegion();
        bindKeyboardNavigation();
        reducedMotionQuery ??= window.matchMedia("(prefers-reduced-motion: reduce)");

        try {
            if (emblaApi) {
                emblaApi.reInit(getEmblaOptions());
                bindSelectListener();
            } else {
                await createEmbla();
            }

            if (!reducedMotionHandler) {
                reducedMotionHandler = () => {
                    destroyEmbla();
                    void createEmbla();
                };
                reducedMotionQuery.addEventListener("change", reducedMotionHandler);
            }
        } catch (error) {
            console.error("Failed to load Embla Carousel:", error);
        }
    }

    async function init() {
        try {
            await refreshCarousel();
        } catch (error) {
            console.error("Failed to initialize carousel:", error);
        }
    }

    init();

    return {
        update(newParams: CarouselActionOptions) {
            params = newParams;
            queueMicrotask(() => {
                if (!destroyed) {
                    void refreshCarousel();
                }
            });
        },
        destroy() {
            destroyed = true;
            unbindKeyboardNavigation();
            destroyLiveRegion();
            if (reducedMotionQuery && reducedMotionHandler) {
                reducedMotionQuery.removeEventListener("change", reducedMotionHandler);
            }
            destroyEmbla();
        },
    };
}
